import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import GlobalStyles from '@mui/material/GlobalStyles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PrintIcon from '@mui/icons-material/Print';
import { QRCodeSVG } from 'qrcode.react';

export interface QrLabelItem {
  code: string;
  title: string;
  subtitle?: string;
}

interface QrLabelPrintDialogProps {
  open: boolean;
  items: QrLabelItem[];
  onClose: () => void;
}

// Physical sticker size — small enough for a rack/bin/zone label, large enough for a handheld
// scanner to read the QR reliably at close range. Matches GrnLabelPrintPage's own @page-per-label
// idiom (see that file's GlobalStyles comment) so the browser paginates one label per sheet.
const LABEL_SIZE_MM = 50;
const PX_PER_MM = 3.7795;

function QrLabel({ item, qrSizePx }: { item: QrLabelItem; qrSizePx: number }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ width: '100%', height: '100%', p: 1, boxSizing: 'border-box', overflow: 'hidden' }}>
      <QRCodeSVG value={item.code} size={qrSizePx} level="M" />
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, textAlign: 'center', lineHeight: 1.2 }}>
        {item.code}
      </Typography>
      {item.subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.1, fontSize: '0.65rem' }}>
          {item.subtitle}
        </Typography>
      )}
    </Stack>
  );
}

export default function QrLabelPrintDialog({ open, items, onClose }: QrLabelPrintDialogProps) {
  const { t } = useTranslation('warehouseMaster');

  const handlePrint = () => {
    // Double-rAF ensures the (just-rendered) print-area DOM has committed before window.print()
    // reads it — same idiom as GrnLabelPrintPage's printBatch effect.
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#qr-label-print-area, #qr-label-print-area *': { visibility: 'visible' },
            '#qr-label-print-area': { position: 'absolute', top: 0, left: 0, margin: 0 },
          },
          '@page': { size: `${LABEL_SIZE_MM}mm ${LABEL_SIZE_MM}mm`, margin: 0 },
        }}
      />

      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('qrPrint.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('qrPrint.hint')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
            {items.map((item, i) => (
              <Box key={i} sx={{ width: 150, height: 150, bgcolor: '#fff', boxShadow: '0px 2px 8px rgba(0,0,0,0.15)', borderRadius: 1 }}>
                <QrLabel item={item} qrSizePx={80} />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('qrPrint.close')}</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={items.length === 0}>
            {t('qrPrint.printButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden except under @media print — one QR label per physical sticker, each its own page
          so the browser's print dialog paginates them naturally onto label stock. */}
      {open && (
        <Box id="qr-label-print-area" sx={{ display: 'none', '@media print': { display: 'block' } }}>
          {items.map((item, i) => (
            <Box
              key={i}
              sx={{
                width: `${LABEL_SIZE_MM}mm`,
                height: `${LABEL_SIZE_MM}mm`,
                breakAfter: i === items.length - 1 ? 'auto' : 'page',
                breakInside: 'avoid',
                pageBreakAfter: i === items.length - 1 ? 'auto' : 'always',
                pageBreakInside: 'avoid',
              }}
            >
              <QrLabel item={item} qrSizePx={Math.round(LABEL_SIZE_MM * PX_PER_MM * 0.55)} />
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}
